import { useEffect, useId, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { HelpTooltip } from '@gonfalon/ui/HelpTooltip';
import {
  Button,
  Code,
  FieldError,
  Label,
  ListBox,
  ListBoxItem,
  Perceivable,
  Popover,
  Select,
  SelectValue,
  Tag,
  TagGroup,
  TagList,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import type { MetricForm } from '../../../CreateMetricModal/formUtils';

import { isNumericColumnType } from './isNumericColumnType';

import styles from './ValueColumnField.module.css';

export const VALUE_COLUMN_HELP_TEXT = (
  <Message id="metrics.metricDefinition.valueColumnHelp" values={{ code: (chunks) => <Code>{chunks}</Code> }} />
);

const mapsValueColumn = (valueColumn?: string) => valueColumn !== undefined && valueColumn.trim() !== '';

export function useIsValueColumnOptional(dataSourceKey?: string): boolean {
  const projectKey = useProjectKey();
  const { data } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  return mapsValueColumn(data?.items?.find((ds) => ds.key === dataSourceKey)?.columnMappings?.valueColumn);
}

interface ValueColumnFieldProps {
  name: 'valueColumn' | 'denominator.valueColumn';
  dataSourceKey?: string;
  /** Id of a label the caller renders instead, such as a row's label in the sectioned layout. */
  'aria-labelledby'?: string;
}

export const ValueColumnField: React.FC<ValueColumnFieldProps> = ({
  name,
  dataSourceKey,
  'aria-labelledby': externalLabelId,
}) => {
  const projectKey = useProjectKey();
  const { data: dataSourcesData } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  const dataSource = dataSourcesData?.items?.find((ds) => ds.key === dataSourceKey);
  const columns = dataSource?.columnMappings?.columns ?? [];
  const dataSourceValueColumn = dataSource?.columnMappings?.valueColumn;

  const { control, trigger, getValues, resetField } = useFormContext<MetricForm>();
  const sourceOwnsColumn = mapsValueColumn(dataSourceValueColumn);
  const isReadOnly = sourceOwnsColumn && columns.length === 0;
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: { required: sourceOwnsColumn ? false : msg('metrics.metricDefinition.valueColumnRequired') },
    shouldUnregister: true,
  });

  useEffect(() => {
    if (sourceOwnsColumn) {
      void trigger(name);
    }
  }, [sourceOwnsColumn, name, trigger]);

  // A value column only means something relative to its data source. Both call sites remount this
  // field on a data source change (`key={dataSourceKey}`), and `shouldUnregister` makes React Hook
  // Form rehydrate it from the form's original defaults — which, when editing an existing metric,
  // still hold the column picked for the previous source. Drop a carried-over column once the new
  // source is known, so the form never posts a column the selected source does not have.
  // Read through `getValues` rather than the watched value: the rehydrate writes to the form store
  // without notifying subscribers, so `field.value` still lags a render behind at this point.
  useEffect(() => {
    if (dataSourcesData === undefined) {
      return;
    }
    const current = getValues(name);
    if (typeof current !== 'string' || current === '') {
      return;
    }

    const sourceColumns = dataSource?.columnMappings?.columns ?? [];
    const isUnverifiable = sourceColumns.length === 0;
    if (isUnverifiable) {
      // An unknown or column-less source is missing evidence, not proof the column is wrong.
      // Clearing here would discard a column the metric legitimately stores, and the Select is
      // disabled in that state so the user could not put it back.
      return;
    }

    if (!sourceColumns.some((column) => column.name === current)) {
      // Deliberately not `field.onChange`: this correction is the field reconciling itself with
      // the selected source, not an edit. Dirtying the form would arm the edit modal's unsaved
      // changes prompt and enable Save over a change the user never made. Suppressing the flag at
      // write time is not enough either — React Hook Form recomputes `isDirty` by comparing the
      // form against its defaults on any later interaction, so the default has to move with the
      // value. `resetField` does both, then discards the field's error, so revalidate to put a
      // required error back when one is now warranted.
      resetField(name, { defaultValue: '' });
      void trigger(name);
    }
  }, [dataSourcesData, dataSource, name, getValues, resetField, trigger]);

  const ownLabelId = useId();
  const labelId = externalLabelId ?? ownLabelId;
  const focusRef = useRef<HTMLButtonElement | null>(null);
  const setTriggerRef = (element: HTMLButtonElement | null) => {
    focusRef.current = element;
    field.ref(element);
  };

  const value = typeof field.value === 'string' ? field.value : '';
  const selectableColumns = columns.filter(
    (column) => isNumericColumnType(column.type) || column.name === dataSourceValueColumn || column.name === value,
  );

  return (
    <div className={styles.field}>
      {externalLabelId === undefined && (
        <span className={styles.labelRow}>
          <Label onClick={() => focusRef.current?.focus()}>
            <div id={ownLabelId} className={clsx({ [styles.required]: !sourceOwnsColumn })}>
              {msg('metrics.metricDefinition.valueColumnLabel')}
            </div>
          </Label>
          <HelpTooltip placement="right">{VALUE_COLUMN_HELP_TEXT}</HelpTooltip>
        </span>
      )}
      {isReadOnly ? (
        <Perceivable isDisabled>
          <TooltipTrigger>
            <Button aria-labelledby={labelId} className={styles.readonlyTrigger} ref={setTriggerRef}>
              <span>{value || dataSourceValueColumn}</span>
              <Icon name="chevron-down" size="small" />
            </Button>
            <Tooltip placement="top">
              <Message id="metrics.metricDefinition.valueColumnLocked" />
            </Tooltip>
          </TooltipTrigger>
        </Perceivable>
      ) : (
        <Select
          aria-labelledby={labelId}
          className={styles.select}
          isInvalid={Boolean(error)}
          isDisabled={selectableColumns.length === 0}
          name={field.name}
          onBlur={field.onBlur}
          selectedKey={value || (sourceOwnsColumn ? (dataSourceValueColumn ?? null) : null)}
          onSelectionChange={(key) => {
            if (typeof key === 'string') {
              field.onChange(sourceOwnsColumn && key === dataSourceValueColumn ? '' : key);
            }
          }}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              void trigger(name);
            }
          }}
        >
          <Button ref={setTriggerRef}>
            <SelectValue>
              {({ defaultChildren, isPlaceholder }) =>
                isPlaceholder ? (
                  <div>
                    {selectableColumns.length > 0
                      ? msg('metrics.metricDefinition.columnPlaceholder')
                      : columns.length > 0
                        ? msg('metrics.metricDefinition.noNumericColumns')
                        : msg('metrics.metricDefinition.noColumns')}
                  </div>
                ) : (
                  defaultChildren
                )
              }
            </SelectValue>
            <Icon name="chevron-down" size="small" />
          </Button>
          <Popover className={styles.popover}>
            <ListBox
              aria-label={msg('metrics.metricDefinition.columnNamesLabel')}
              renderEmptyState={() => msg('metrics.metricDefinition.noColumnsEmptyState')}
            >
              {selectableColumns.map((column) => (
                <ListBoxItem key={column.name} id={column.name} textValue={column.name}>
                  <div className={styles.itemContents}>
                    {column.name}
                    <Text slot="description">
                      <TagGroup aria-label={msg('metrics.metricDefinition.columnTypeLabel')}>
                        <TagList>
                          <Tag className={styles.tag}>{column.type}</Tag>
                        </TagList>
                      </TagGroup>
                    </Text>
                  </div>
                </ListBoxItem>
              ))}
            </ListBox>
          </Popover>
          {Boolean(error) && <FieldError>{error?.message}</FieldError>}
        </Select>
      )}
    </div>
  );
};
